/**
 * One company list (prototype board 3): its companies, and the company side
 * panel over them.
 *
 * The panel is the person panel's drawer (`PersonPanel`, with `noun` and
 * `testIdPrefix` for a company): ‹ › and ←/→ walk the rows of the current page,
 * Escape closes, and `?company=<id>` carries the open company so the view can
 * be shared and survives a reload. "Open as page" goes to the company record
 * (`/companies/:companyId`).
 *
 * Filters (Trello #831), laid out like People Lists' detail page: name or
 * website, employee and revenue ranges, "We know" and "LinkedIn". They and the
 * page live in the URL (`useCompanyMembersFilters`), beside `?company=`; the
 * filtered total drives the pager, a filter change goes back to page 1, and
 * the panel's ‹ › walk the filtered rows.
 */
import type { CompanyListMember } from "@nu-assist/contracts";
import { listPageSize } from "@nu-assist/shared-fe";
import { Button, ProgressBar, Spinner } from "@nu-assist/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Download, Pencil, X } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  type CompanyListsClient,
  type CompanyMemberSortKey,
  createCompanyListsClient,
} from "../../clients/companyLists.client";
import { TablePagination } from "../../components/TablePagination";
import { CompanyMembersFilterBar } from "../../features/company-lists/CompanyMembersFilterBar";
import { CompanyMembersTable } from "../../features/company-lists/CompanyMembersTable";
import { CompanyView } from "../../features/company-lists/CompanyView";
import { MatchWithLinkedin } from "../../features/company-lists/MatchWithLinkedin";
import {
  NoWebsiteOrLinkedinNote,
  hasNoWebsiteOrLinkedin,
} from "../../features/company-lists/NoWebsiteOrLinkedinNote";
import type { ChopLink } from "../../features/company-lists/chopLink";
import { formatDate, sourceLabel } from "../../features/company-lists/format";
import { COMPANY_LISTS_PATH, companyPagePath } from "../../features/company-lists/paths";
import {
  COMPANY_LISTS_KEY,
  useCompanyList,
  useCompanyListMembers,
  useLogoUrls,
} from "../../features/company-lists/queries";
import { saveBlob } from "../../features/company-lists/saveBlob";
import { useCompanyMembersFilters } from "../../features/company-lists/useCompanyMembersFilters";
import { useCompanyPanelUrl } from "../../features/company-lists/useCompanyPanelUrl";
import { PersonPanel } from "../../features/person-panel/PersonPanel";
import { searchDetailPath } from "../../routing/paths";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
const defaultClient = createCompanyListsClient({ apiBase: API_BASE });

interface CompanyListDetailPageProps {
  client?: CompanyListsClient;
  /** Injected in tests; the page talks to the real ChopLink otherwise. */
  chopLink?: ChopLink;
}

function backTarget(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith("/") ? from : COMPANY_LISTS_PATH;
}

/**
 * Keyed by list id: the route reuses this element when `:listId` changes, and
 * without the key the filter boxes' drafts (seeded once from the URL) would
 * carry list A's filter into list B's URL on the next debounce. A new list is
 * a new page.
 */
export function CompanyListDetailPage(props: CompanyListDetailPageProps) {
  const { listId = "" } = useParams<{ listId: string }>();
  return <CompanyListDetail key={listId} listId={listId} {...props} />;
}

function CompanyListDetail({
  client = defaultClient,
  chopLink,
  listId,
}: CompanyListDetailPageProps & { listId: string }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const pageSize = listPageSize();
  const filters = useCompanyMembersFilters();
  const { page, goToPage, sort } = filters;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const panel = useCompanyPanelUrl();
  const back = useMemo(() => backTarget(location.state), [location.state]);

  const listQuery = useCompanyList(client, listId);
  const membersQuery = useCompanyListMembers({
    client,
    listId,
    page,
    pageSize,
    sort,
    filters: filters.params,
  });
  const list = listQuery.data;
  const items = membersQuery.data?.items ?? [];
  const total = membersQuery.data?.total ?? 0;
  const logoUrls = useLogoUrls(client, items);

  const refresh = () => queryClient.invalidateQueries({ queryKey: COMPANY_LISTS_KEY });

  const rename = useMutation({
    mutationFn: (name: string) => client.rename(listId, name),
    onSuccess: () => {
      setEditing(false);
      void refresh();
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Couldn’t rename."),
  });
  const exportCsv = useMutation({
    mutationFn: () => client.exportCsv(listId),
    onSuccess: ({ filename, blob }) => saveBlob(filename, blob),
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Couldn’t export."),
  });

  // The list can shrink under the open page (another tab, a merge, or a URL
  // whose page is past the filtered end): step back to the new last page.
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (membersQuery.isSuccess && !membersQuery.isFetching && page > pageCount) goToPage(pageCount);
  }, [membersQuery.isSuccess, membersQuery.isFetching, page, pageCount, goToPage]);

  // Focus returns to the row that opened the panel when it closes.
  const openerRef = useRef<string | null>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (panel.companyId) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const id = openerRef.current;
    if (id) document.querySelector<HTMLElement>(`[data-company-row="${id}"]`)?.focus();
  }, [panel.companyId]);

  const openIndex = panel.companyId ? items.findIndex((i) => i.company_id === panel.companyId) : -1;
  const openCompany = openIndex >= 0 ? items[openIndex] : undefined;

  function belowName(row: CompanyListMember) {
    return hasNoWebsiteOrLinkedin(row) ? <NoWebsiteOrLinkedinNote /> : null;
  }

  function openRow(companyId: string) {
    openerRef.current = companyId;
    if (panel.companyId) panel.move(companyId);
    else panel.open(companyId);
  }
  function step(delta: number) {
    const next = items[openIndex + delta];
    if (!next) return;
    openerRef.current = next.company_id;
    panel.move(next.company_id);
  }
  function onSort(key: CompanyMemberSortKey) {
    // One URL write: the sort, and back to page 1.
    filters.toggleSort(key);
  }
  function submitRename(e: FormEvent) {
    e.preventDefault();
    const name = draft.replace(/\s+/g, " ").trim();
    if (name && list && name !== list.name) rename.mutate(name);
    else setEditing(false);
  }

  if (listQuery.isPending) {
    return (
      <div className="flex justify-center p-12">
        <Spinner label="Loading company list…" />
      </div>
    );
  }
  if (!list) {
    return (
      <div className="flex flex-col gap-4 p-8" data-testid="company-list-missing">
        <Link
          to={back}
          className="inline-flex items-center gap-1 text-label text-text-secondary hover:text-text"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back
        </Link>
        <p className="text-body text-text-secondary">
          This company list doesn’t exist or was deleted.
        </p>
      </div>
    );
  }

  const settling = list.status === "uploaded" || list.status === "parsing";

  return (
    <div className="flex flex-col gap-4 p-8" data-testid="company-list-page">
      <Link
        to={back}
        className="inline-flex w-fit items-center gap-1 text-label text-text-secondary hover:text-text"
        data-testid="company-list-back"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Company lists
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          {editing ? (
            <form onSubmit={submitRename} className="flex items-center gap-2">
              <input
                // biome-ignore lint/a11y/noAutofocus: the user just asked to rename.
                autoFocus
                aria-label="List name"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={240}
                data-testid="company-list-rename-input"
                className="rounded-md border border-border bg-bg px-3 py-1.5 text-h2 text-text focus:border-accent focus:outline-none"
              />
              <Button
                type="submit"
                variant="secondary"
                aria-label="Save name"
                disabled={rename.isPending}
              >
                <Check className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                aria-label="Cancel rename"
                onClick={() => setEditing(false)}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-h1 text-text" data-testid="company-list-title">
                {list.name}
              </h1>
              <button
                type="button"
                aria-label="Rename list"
                onClick={() => {
                  setDraft(list.name);
                  setEditing(true);
                }}
                className="rounded-md p-1 text-text-muted hover:bg-surface-raised hover:text-text"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
          <p className="flex flex-wrap items-center gap-x-2 text-label text-text-secondary">
            <span>{sourceLabel(list.source)}</span>
            <span aria-hidden="true">·</span>
            <span data-testid="company-list-count">
              {list.company_count.toLocaleString()}{" "}
              {list.company_count === 1 ? "company" : "companies"}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {formatDate(list.created_at)} by {list.created_by_name ?? list.created_by_email}
            </span>
            {list.search_id_thrive && (
              <>
                <span aria-hidden="true">·</span>
                <Link
                  to={searchDetailPath(list.search_id_thrive)}
                  className="hover:text-accent-ink hover:underline"
                >
                  {list.search_name ?? `Search ${list.search_id_thrive}`}
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-start gap-2">
          <MatchWithLinkedin client={client} listId={listId} chopLink={chopLink} />
          <Button
            variant="secondary"
            onClick={() => exportCsv.mutate()}
            disabled={exportCsv.isPending || list.company_count === 0}
            data-testid="company-list-export"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {settling && (
        <div
          data-testid="company-list-importing"
          className="flex flex-col gap-2 rounded-md border border-border bg-surface px-4 py-3 text-body text-text-secondary"
        >
          <p>Importing companies… this list fills in as they arrive.</p>
          <ProgressBar indeterminate live label="Importing companies" />
        </div>
      )}
      {list.status === "failed" && (
        <p className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error">
          The import failed. Companies added before the failure are shown below.
        </p>
      )}

      <CompanyMembersFilterBar filters={filters} total={list.company_count} />

      {membersQuery.isError && !membersQuery.data ? (
        <div className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error">
          Couldn’t load the companies. Please try again.
        </div>
      ) : membersQuery.isPending || (membersQuery.isPlaceholderData && items.length === 0) ? (
        // Also while a filter change loads with nothing to show yet:
        // `placeholderData` keeps the previous (empty) page, and its empty
        // state under the NEW filters would flash the wrong message (as
        // `CompanyListsView` guards it).
        <div className="flex justify-center py-12">
          <Spinner label="Loading companies…" />
        </div>
      ) : (
        <>
          <CompanyMembersTable
            items={items}
            sort={sort}
            onSort={onSort}
            openCompanyId={panel.companyId}
            onOpen={openRow}
            logoUrls={logoUrls}
            belowName={belowName}
            filtered={filters.active}
          />
          <TablePagination
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={goToPage}
            noun="company"
            nounPlural="companies"
            testId="company-members-pagination"
          />
        </>
      )}

      {panel.companyId && (
        <PersonPanel
          noun={{ singular: "Company", lower: "company" }}
          testIdPrefix="company-panel"
          size="half"
          pagePath={companyPagePath(panel.companyId)}
          listTitle={list.name}
          position={openIndex >= 0 ? { index: openIndex, total: items.length } : null}
          positionLoading={membersQuery.isPending}
          positionError={membersQuery.isError && !membersQuery.data}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          onClose={panel.close}
          shareLinks={[
            {
              id: "list",
              label: "Link to this company in the list",
              description: "Opens the list with this company's panel.",
              // The filters ride along: the panel's ‹ › walk the filtered rows.
              url: `${window.location.origin}${location.pathname}${location.search}`,
            },
          ]}
        >
          {(nameId) =>
            openCompany ? (
              <CompanyView
                key={openCompany.company_id}
                client={client}
                list={list}
                company={openCompany}
                nameId={nameId}
              />
            ) : (
              <p className="p-6 text-body text-text-secondary" id={nameId}>
                {membersQuery.isPending
                  ? "Loading…"
                  : "This company is not on the page of the list you are looking at."}
              </p>
            )
          }
        </PersonPanel>
      )}
    </div>
  );
}
